import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, RefreshCw, DownloadCloud, Users, Wallet, TrendingUp, Target, Info } from 'lucide-react';
import { getPreviewAuthHeaders } from '../lib/firebase';
import { AdminStudents } from './AdminStudents';
import {
  AnalyticsData, NAVY, TEAL, RANGES, PLAN_NAMES, fmtMoney, formatOne, moneyValue, pct, monthLabel, dayLabel,
  Card, Stat, Columns, HBar
} from './AnalyticsParts';

// =====================================================================
// Admin Analytics (round A, 2026-10-25): conversion funnel, revenue,
// growth and target exam demand. Data comes from GET /api/admin/analytics,
// which only answers for the admin account. Round B adds the Students
// view (AdminStudents.tsx). Charts are plain divs so no new packages.
// =====================================================================

const AnalyticsOverview: React.FC = () => {
  const [range, setRange] = useState('30');
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [examSort, setExamSort] = useState<'students' | 'paid' | 'next30'>('students');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const headers = await getPreviewAuthHeaders();
      const res = await fetch(`/api/admin/analytics?range=${range}${refresh ? '&refresh=1' : ''}`, { headers });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `The server answered ${res.status}`);
      if (!Array.isArray(body?.funnel)) throw new Error('The server is running an older version. Upload the latest server.ts.');
      setData(body as AnalyticsData);
      const cs: string[] = body?.revenue?.currencies || [];
      if (cs.length && !cs.includes(currency)) setCurrency(cs[0]);
    } catch (e: any) {
      setError(e?.message || 'Could not load analytics');
    } finally {
      setLoading(false);
    }
  }, [range, currency]);

  useEffect(() => { load(false); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncPayments = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      const headers = await getPreviewAuthHeaders();
      const res = await fetch('/api/admin/payments/sync', { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: '{}' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `The server answered ${res.status}`);
      setSyncMessage(`Checked ${body.transactionsChecked ?? 0} Paystack payments and added ${body.added ?? 0} that were missing.${body.withoutStudentId ? ` ${body.withoutStudentId} had no student id attached, so they count in revenue only.` : ''}`);
      await load(true);
    } catch (e: any) {
      setSyncMessage(`Sync did not finish: ${e?.message || 'unknown error'}`);
    } finally {
      setSyncing(false);
    }
  };

  const rangeLabel = RANGES.find((r) => r.value === range)?.label || '';

  if (!data && loading) {
    return <div className="py-16 text-center text-slate-500 text-sm">Loading analytics…</div>;
  }
  if (!data) {
    return (
      <div className="py-16 text-center">
        <p className="text-slate-700 font-semibold">Analytics could not load.</p>
        <p className="text-sm text-slate-500 mt-1">{error}</p>
        <button onClick={() => load(true)} className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-bold cursor-pointer" style={{ background: TEAL }}>Try again</button>
      </div>
    );
  }

  const funnelTop = data.funnel[0]?.count || 0;
  const signupCount = data.funnel.find((f) => f.key === 'signups')?.count || 0;
  const paidCount = data.funnel.find((f) => f.key === 'paid')?.count || 0;
  const currencies = data.revenue.currencies.length ? data.revenue.currencies : ['USD'];
  const exams = [...data.targetExams.exams].sort((a, b) => (b[examSort] - a[examSort]) || (b.students - a.students));
  const famMax = Math.max(1, ...data.targetExams.families.map((f) => f.students));
  const planMax = Math.max(1, ...data.revenue.byPlan.map((p) => moneyValue(p.revenue, currency)));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500">
            Updated {new Date(data.generatedAt).toLocaleString()} · {data.growth.totalUsers.toLocaleString()} accounts in total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-xl border border-slate-200 bg-white p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition ${range === r.value ? 'text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                style={range === r.value ? { background: NAVY } : undefined}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={() => load(true)} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm px-4 py-2.5">{error}</div>}

      {/* ---------------- Funnel ---------------- */}
      <Card title={`Conversion funnel, last ${rangeLabel.toLowerCase()}`} icon={Users}>
        <div className="grid md:grid-cols-[1fr_220px] gap-6">
          <div className="space-y-3">
            {data.funnel.map((step, i) => {
              const prev = i > 0 ? data.funnel[i - 1].count : 0;
              return (
                <div key={step.key}>
                  <HBar
                    label={step.label}
                    value={step.count}
                    max={Math.max(funnelTop, ...data.funnel.map((f) => f.count))}
                    right={step.count.toLocaleString()}
                    color={step.key === 'paid' ? NAVY : TEAL}
                  />
                  {i > 0 && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{pct(step.count, prev)} of the step before</div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-1 gap-3 content-start">
            <Stat label="Sign up to paid" value={pct(paidCount, signupCount)} sub={`${paidCount} of ${signupCount} new accounts`} />
            <Stat label="Median days to pay" value={data.growth.medianDaysToFirstPayment === null ? 'None yet' : `${data.growth.medianDaysToFirstPayment}`} sub="From sign up to first payment" />
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-4 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          Guests are visitors who tried the free questions without an account ({data.notes.guestsAllTime.toLocaleString()} all time). "Practised" means the account answered at least one question.
        </p>
      </Card>

      {/* ---------------- Revenue ---------------- */}
      <Card
        title="Revenue"
        icon={Wallet}
        right={
          <div className="flex items-center gap-2">
            {currencies.length > 1 && (
              <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
                {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <button
              onClick={syncPayments}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white text-xs font-bold cursor-pointer disabled:opacity-60"
              style={{ background: TEAL }}
            >
              <DownloadCloud className={`w-3.5 h-3.5 ${syncing ? 'animate-pulse' : ''}`} />
              {syncing ? 'Syncing…' : 'Sync past payments from Paystack'}
            </button>
          </div>
        }
      >
        {syncMessage && <div className="mb-4 rounded-lg bg-teal-50 border border-teal-200 text-teal-900 text-xs px-3 py-2">{syncMessage}</div>}
        {data.revenue.paymentsCount === 0 && (
          <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 text-slate-600 text-xs px-3 py-2">
            No payments are recorded yet. Press "Sync past payments from Paystack" once to bring in everything paid before this screen existed.
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="This month" value={fmtMoney(data.revenue.thisMonth, currency)} />
          <Stat label="Last month" value={fmtMoney(data.revenue.lastMonth, currency)} />
          <Stat label={`Last ${rangeLabel.toLowerCase()}`} value={fmtMoney(data.revenue.inRange, currency)} />
          <Stat label="All time" value={fmtMoney(data.revenue.total, currency)} sub={`${data.revenue.paymentsCount} payments: ${data.revenue.newPayments} first time, ${data.revenue.renewals} renewing`} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Stat label="Active subscribers" value={data.revenue.activeSubscribers.toLocaleString()} />
          <Stat label="Expired subscribers" value={data.revenue.expiredSubscribers.toLocaleString()} sub="Paid before, access has ended" />
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 mt-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Monthly revenue ({currency}), last 12 months</h3>
            <Columns
              items={data.revenue.monthly.map((m) => ({
                label: monthLabel(m.month),
                value: moneyValue(m.revenue, currency),
                tip: `${monthLabel(m.month)} ${m.month.slice(0, 4)}: ${formatOne(moneyValue(m.revenue, currency), currency)}, ${m.payments} payment${m.payments === 1 ? '' : 's'}`
              }))}
            />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">By plan, all time</h3>
            <div className="space-y-3">
              {data.revenue.byPlan.length === 0 && <p className="text-sm text-slate-400">No plan sales yet.</p>}
              {data.revenue.byPlan.map((p) => (
                <HBar
                  key={p.tier}
                  label={PLAN_NAMES[p.tier] || p.tier}
                  value={moneyValue(p.revenue, currency)}
                  max={planMax}
                  right={`${formatOne(moneyValue(p.revenue, currency), currency)} · ${p.payments}`}
                  tip={`${p.payments} payments`}
                />
              ))}
            </div>
          </div>
        </div>

        {data.revenue.recent.length > 0 && (
          <div className="mt-6">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">Latest payments</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">Date</th>
                    <th className="py-2 pr-3 font-semibold">Student</th>
                    <th className="py-2 pr-3 font-semibold">Plan</th>
                    <th className="py-2 pr-3 font-semibold text-right">Amount</th>
                    <th className="py-2 font-semibold">Paid with</th>
                  </tr>
                </thead>
                <tbody>
                  {data.revenue.recent.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{r.paidAt ? dayLabel(r.paidAt) : ''}</td>
                      <td className="py-2 pr-3 text-slate-800 truncate max-w-[220px]">{r.email || 'Unknown'}</td>
                      <td className="py-2 pr-3 text-slate-700">{PLAN_NAMES[r.tier] || r.tier || 'Unknown'}</td>
                      <td className="py-2 pr-3 text-right font-bold tabular-nums text-slate-900">{formatOne(r.amount, r.currency)}</td>
                      <td className="py-2 text-slate-500">{[r.channel, r.country].filter(Boolean).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>

      {/* ---------------- Growth ---------------- */}
      <Card title="Growth, last 12 weeks" icon={TrendingUp}>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">New accounts per week</h3>
            <Columns
              items={data.growth.weekly.map((w) => ({
                label: dayLabel(w.weekStart),
                value: w.signups,
                tip: `Week of ${dayLabel(w.weekStart)}: ${w.signups} sign up${w.signups === 1 ? '' : 's'}`
              }))}
            />
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">First payments per week</h3>
            <Columns
              items={data.growth.weekly.map((w) => ({
                label: dayLabel(w.weekStart),
                value: w.paid,
                tip: `Week of ${dayLabel(w.weekStart)}: ${w.paid} first payment${w.paid === 1 ? '' : 's'}`
              }))}
            />
          </div>
        </div>
      </Card>

      {/* ---------------- Target exams ---------------- */}
      <Card
        title="Target exams"
        icon={Target}
        right={
          <select value={examSort} onChange={(e) => setExamSort(e.target.value as any)} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white">
            <option value="students">Sort by students</option>
            <option value="paid">Sort by paid</option>
            <option value="next30">Sort by exam in 30 days</option>
          </select>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3 font-semibold">Exam</th>
                <th className="py-2 pr-3 font-semibold text-right">Students</th>
                <th className="py-2 pr-3 font-semibold text-right">Paid</th>
                <th className="py-2 pr-3 font-semibold text-right">Conversion</th>
                <th className="py-2 pr-3 font-semibold text-right">Exam in 30 days</th>
                <th className="py-2 font-semibold text-right">Exam in 90 days</th>
              </tr>
            </thead>
            <tbody>
              {exams.length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-slate-400">No students have picked a goal exam yet.</td></tr>
              )}
              {exams.map((e) => (
                <tr key={e.key} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="py-2 pr-3 text-slate-800">
                    <div className="font-semibold">{e.label}</div>
                    {e.family && <div className="text-[11px] text-slate-400">{e.family}</div>}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums">{e.students}</td>
                  <td className="py-2 pr-3 text-right tabular-nums font-bold text-slate-900">{e.paid}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{e.conversion || 0}%</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{e.next30 || ''}</td>
                  <td className="py-2 text-right tabular-nums">{e.next90 || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.targetExams.noGoal > 0 && (
          <p className="text-[11px] text-slate-400 mt-2">{data.targetExams.noGoal} accounts have not picked a goal exam.</p>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Demand by exam family</h3>
            <div className="space-y-3">
              {data.targetExams.families.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
              {data.targetExams.families.map((f) => (
                <HBar key={f.family} label={f.family} value={f.students} max={famMax} right={`${f.students}`} />
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Free questions against paid</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-3 font-semibold">Paper</th>
                    <th className="py-2 pr-3 font-semibold text-right">Guests</th>
                    <th className="py-2 pr-3 font-semibold text-right">Accounts</th>
                    <th className="py-2 font-semibold text-right">Paid with this goal</th>
                  </tr>
                </thead>
                <tbody>
                  {data.targetExams.freeQuestionUse.length === 0 && (
                    <tr><td colSpan={4} className="py-4 text-center text-slate-400">No free questions used yet.</td></tr>
                  )}
                  {data.targetExams.freeQuestionUse.slice(0, 15).map((r) => (
                    <tr key={r.paperId} className="border-b border-slate-100 last:border-0">
                      <td className="py-2 pr-3 text-slate-800 truncate max-w-[200px]" title={`${r.questionsAnswered} free questions answered`}>{r.label}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.guests}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{r.accounts}</td>
                      <td className="py-2 text-right tabular-nums font-bold text-slate-900">{r.paidWithThisGoal}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Analytics screen with two views: the overview (round A) and the
// students table with profiles (round B).
export const AdminAnalytics: React.FC = () => {
  const [view, setView] = useState<'overview' | 'students'>('overview');
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-extrabold" style={{ color: NAVY }}>
          <BarChart3 className="w-6 h-6" style={{ color: TEAL }} /> Analytics
        </h1>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1" role="tablist">
          {([['overview', 'Overview'], ['students', 'Students']] as const).map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={view === id}
              onClick={() => setView(id)}
              className={`px-4 py-1.5 rounded-lg text-sm font-bold cursor-pointer transition ${view === id ? 'text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              style={view === id ? { background: TEAL } : undefined}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {view === 'overview' ? <AnalyticsOverview /> : <AdminStudents />}
    </div>
  );
};

export default AdminAnalytics;
